import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { cn } from "../lib/utils";
import { Locations, Users, AdminOps, apiErrorMessage } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import { ROLES, ROLE_LABELS } from "../lib/constants";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Users as UsersIcon, MapPin, ShieldAlert as ShieldAlertIcon, BookOpen, Power, Bell } from "lucide-react";
import MastersPanel from "../components/MastersPanel";
import SlaMastersPanel from "../components/SlaMastersPanel";

export default function Admin() {
  const { locations, users, refreshDirectory, activeUser } = useApp();

  const isAdmin = activeUser?.role === "Admin";
  const isSM = activeUser?.role === "SalesManager";

  // Sales Managers can only see + manage users whose locations are a subset
  // of their own — mirrors the backend scope guard.
  const smLocSet = new Set(activeUser?.location_ids || []);
  const scopedUsers = isSM
    ? users.filter((u) => {
        if (u.role === "Admin" || u.role === "SalesManager") return false;
        const uLocs = u.location_ids || [];
        return uLocs.length > 0 && uLocs.every((l) => smLocSet.has(l));
      })
    : users;
  const scopedLocations = isSM
    ? locations.filter((l) => smLocSet.has(l.id))
    : locations;

  // Build the extra Masters categories for Locations (Admin-only) + Users.
  // These render inside the Masters panel's right pane instead of a separate
  // top-level card, so the whole admin console is now unified under Masters.
  const extraGroups = useMemo(() => {
    const items = [];
    if (isAdmin) {
      items.push({
        key: "location",
        label: "Locations",
        icon: MapPin,
        count: locations.length,
        render: () => (
          <LocationsPanel
            locations={locations}
            refresh={refreshDirectory}
            embedded
          />
        ),
      });
    }
    items.push({
      key: "user",
      label: "Users",
      icon: UsersIcon,
      count: scopedUsers.length,
      render: () => (
        <UsersPanel
          users={scopedUsers}
          locations={scopedLocations}
          refresh={refreshDirectory}
          canDelete={isAdmin}
          role={activeUser?.role}
          embedded
        />
      ),
    });
    return [{ key: "access", label: "Access & Setup", items }];
  }, [isAdmin, locations, scopedUsers, scopedLocations, refreshDirectory, activeUser?.role]);

  // Sales Managers get the same operational surface as Admin, minus the
  // Danger Zone (factory reset), location management, and record deletes.
  if (!isAdmin && !isSM) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-6 text-center">
        <p className="text-sm font-medium text-amber-800">
          You need to sign in as an <strong>Admin</strong> or <strong>Sales Manager</strong> user to access this page.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-page">
      <div>
        <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-slate-500">
          {isAdmin ? "Administration" : "Sales Manager Console"}
        </div>
        <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight">
          Masters & Setup
        </h1>
        <p className="mt-1 text-xs text-slate-500">
          {isSM
            ? <>Locations, users within your scope, and every dropdown value the app uses — all in one place. Deletion, adding new locations, and factory reset are Admin-only.</>
            : <>Locations, users and every dropdown value the app uses — all in one place. Click the <Pencil className="inline h-3 w-3 align-text-bottom" /> icon to edit; changes apply immediately.</>}
        </p>
      </div>

      {/* Documentation entry — Sales Workflow Stages Master */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <Link
          to="/admin/workflow-stages"
          data-testid="admin-link-workflow-stages"
          className="flex items-center justify-between gap-4 rounded-lg border border-slate-200 bg-gradient-to-r from-slate-50 to-white px-4 py-3 hover:border-primary/40 hover:bg-primary/[0.03]"
        >
          <div className="flex items-center gap-3">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary">
              <BookOpen className="h-4 w-4" />
            </span>
            <div>
              <div className="text-sm font-semibold text-slate-800">
                Sales Workflow Stages Master
              </div>
              <div className="text-[11px] text-slate-500">
                Documentation · every stage / status that appears on a sale and when.
              </div>
            </div>
          </div>
          <span className="text-xs font-semibold text-primary">Open →</span>
        </Link>

        {isAdmin && (
          <Link
            to="/admin/sla-escalations"
            data-testid="admin-link-sla-escalations"
            className="flex items-center justify-between gap-4 rounded-lg border border-slate-200 bg-gradient-to-r from-rose-50 to-white px-4 py-3 hover:border-rose-400/40 hover:bg-rose-50/60"
          >
            <div className="flex items-center gap-3">
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-rose-100 text-rose-700">
                <Bell className="h-4 w-4" />
              </span>
              <div>
                <div className="text-sm font-semibold text-slate-800">
                  SLA Escalation Log
                </div>
                <div className="text-[11px] text-slate-500">
                  Notifications · every WhatsApp alert the escalation matrix has fired.
                </div>
              </div>
            </div>
            <span className="text-xs font-semibold text-rose-700">Open →</span>
          </Link>
        )}
      </div>

      <MastersPanel canDelete={isAdmin} extraGroups={extraGroups} />

      {isAdmin && <SlaMastersPanel />}

      {isAdmin && <DangerZone refresh={refreshDirectory} activeUser={activeUser} />}
    </div>
  );
}

// ─────────── Danger Zone ───────────

function DangerZone({ refresh, activeUser }) {
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const navigate = useNavigate();

  const CONFIRM = "RESET ALL DATA";
  const canSubmit = confirmText.trim() === CONFIRM && !submitting;

  const onConfirm = async () => {
    setSubmitting(true);
    try {
      const res = await AdminOps.factoryReset(CONFIRM);
      setResult(res);
      toast.success("Factory reset completed");
      await refresh?.();
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Reset failed");
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    setOpen(false);
    setConfirmText("");
    if (result) {
      setResult(null);
      // After a reset, kick to dashboard to refresh all stats.
      navigate("/");
    }
  };

  return (
    <Card className="border-rose-200 bg-rose-50/30" data-testid="danger-zone-card">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <ShieldAlertIcon className="h-4 w-4 text-rose-600" />
          <CardTitle className="font-display text-lg text-rose-900">Danger Zone</CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="max-w-2xl space-y-1">
            <div className="text-sm font-medium text-rose-900">Factory Reset</div>
            <p className="text-xs text-rose-800/80">
              Permanently deletes all sales, tasks, audit logs, uploaded files, masters, holidays,
              and every non-Admin user. <strong>Locations and working hours are kept.</strong>{" "}
              The seeded Admin account (<span className="font-mono-data">{activeUser?.mobile || "—"}</span>) is preserved.
              This action cannot be undone.
            </p>
          </div>
          <Button
            variant="destructive"
            className="gap-2"
            onClick={() => setOpen(true)}
            data-testid="factory-reset-btn"
          >
            <ShieldAlertIcon className="h-4 w-4" />
            Factory Reset
          </Button>
        </div>
      </CardContent>

      <Dialog open={open} onOpenChange={(o) => !o && close()}>
        <DialogContent className="sm:max-w-md" data-testid="factory-reset-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700">
              <ShieldAlertIcon className="h-5 w-5" />
              {result ? "Reset Complete" : "Confirm Factory Reset"}
            </DialogTitle>
          </DialogHeader>

          {!result ? (
            <div className="space-y-3">
              <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-xs text-rose-800">
                You are about to permanently delete all operational data:
                <ul className="ml-4 mt-2 list-disc space-y-0.5">
                  <li>All sales (drafts + submitted + delivered)</li>
                  <li>All tasks, audit logs and uploaded files</li>
                  <li>All masters (vehicle models, finance partners, etc.)</li>
                  <li>All holidays</li>
                  <li>All users except the seeded Admin</li>
                </ul>
                <p className="mt-2 font-semibold">Locations & working hours are kept.</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">
                  Type <span className="font-mono-data font-semibold text-rose-700">{CONFIRM}</span> to confirm
                </Label>
                <Input
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder={CONFIRM}
                  autoFocus
                  data-testid="factory-reset-confirm-input"
                />
              </div>
            </div>
          ) : (
            <div className="space-y-2 text-xs text-slate-700">
              <p className="text-sm font-medium text-emerald-700">
                Reset completed successfully.
              </p>
              <div className="rounded-md border border-slate-200 bg-slate-50 p-3 font-mono-data text-[11px] leading-relaxed text-slate-700"
                   data-testid="factory-reset-result">
                <div>sales deleted: <strong>{result.sales}</strong></div>
                <div>tasks deleted: <strong>{result.tasks}</strong></div>
                <div>audit logs deleted: <strong>{result.audit_logs}</strong></div>
                <div>sale files deleted: <strong>{result.sale_files}</strong></div>
                <div>masters deleted: <strong>{result.masters}</strong></div>
                <div>holidays deleted: <strong>{result.holidays}</strong></div>
                <div>users removed: <strong>{result.users_removed}</strong></div>
                <div className="mt-1 pt-1 border-t border-slate-200">
                  locations preserved: <strong>{result.locations_preserved}</strong>
                </div>
                <div>working hours preserved: <strong>{result.working_hours_preserved}</strong></div>
                <div>admin preserved: <strong>yes</strong></div>
              </div>
            </div>
          )}

          <DialogFooter>
            {!result ? (
              <>
                <Button variant="outline" onClick={close}>Cancel</Button>
                <Button
                  variant="destructive"
                  disabled={!canSubmit}
                  onClick={onConfirm}
                  data-testid="factory-reset-confirm-btn"
                >
                  {submitting ? "Resetting..." : "Confirm Reset"}
                </Button>
              </>
            ) : (
              <Button onClick={close} data-testid="factory-reset-close-btn">Done</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ─────────── Locations ───────────

function LocationsPanel({ locations, refresh, embedded = false }) {
  const [editing, setEditing] = useState(null);  // null | "new" | {id, name, code}

  const remove = async (id) => {
    if (!window.confirm("Delete this location?")) return;
    try {
      await Locations.remove(id);
      toast.success("Location removed");
      await refresh();
    } catch (e) { toast.error(apiErrorMessage(e) || "Failed"); }
  };

  const header = (
    <div className="flex flex-row items-center justify-between gap-2 pb-3">
      <div className="flex items-center gap-2">
        <MapPin className="h-4 w-4 text-primary" />
        <div>
          <div className="font-display text-base font-semibold text-slate-900">Locations</div>
          <div className="text-[11px] text-slate-500">
            {locations.length} total
          </div>
        </div>
      </div>
      <Button size="sm" className="gap-1" data-testid="add-location-btn"
              onClick={() => setEditing("new")}>
        <Plus className="h-3.5 w-3.5" /> Add Location
      </Button>
    </div>
  );

  const list = (
    <div className={cn(embedded ? "rounded-md border border-slate-200 bg-white" : "")}>
      {locations.length === 0 ? (
        <div className="px-5 py-8 text-center text-sm text-slate-500">No locations yet.</div>
      ) : (
        <ul className={cn(embedded ? "divide-y divide-slate-100" : "divide-y divide-border")}>
          {locations.map((l) => (
            <li key={l.id}
                className={cn("flex items-center justify-between", embedded ? "px-4 py-2.5" : "px-5 py-3")}
                data-testid={`loc-row-${l.code}`}>
              <div>
                <div className="text-sm font-medium text-slate-900">{l.name}</div>
                <div className="font-mono-data text-xs text-slate-500">{l.code}</div>
              </div>
              <div className="flex items-center gap-1">
                <Button size="icon" variant="ghost" onClick={() => setEditing(l)}
                        className="h-8 w-8 text-slate-400 hover:text-primary"
                        data-testid={`edit-location-${l.code}`}>
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button size="icon" variant="ghost" onClick={() => remove(l.id)}
                        className="h-8 w-8 text-slate-400 hover:text-red-600"
                        data-testid={`delete-location-${l.code}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  const dialog = (
    <LocationDialog
      open={editing !== null}
      editing={editing === "new" ? null : editing}
      onClose={() => setEditing(null)}
      onSaved={refresh}
    />
  );

  if (embedded) {
    return (
      <div className="space-y-3" data-testid="locations-panel-embedded">
        {header}
        {list}
        {dialog}
      </div>
    );
  }

  return (
    <Card className="border-slate-200">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <div className="flex items-center gap-2">
          <MapPin className="h-4 w-4 text-primary" />
          <CardTitle className="font-display text-lg">Locations</CardTitle>
        </div>
        <Button size="sm" className="gap-1" data-testid="add-location-btn-alt"
                onClick={() => setEditing("new")}>
          <Plus className="h-3.5 w-3.5" /> Add
        </Button>
      </CardHeader>
      <CardContent className="p-0">{list}</CardContent>
      {dialog}
    </Card>
  );
}

function LocationDialog({ open, editing, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const isEdit = !!editing;

  useEffect(() => {
    if (open) {
      setName(editing?.name || "");
      setCode(editing?.code || "");
    }
  }, [open, editing]);

  const save = async () => {
    if (!name.trim() || !code.trim()) return toast.error("Name and code required");
    try {
      const payload = { name: name.trim(), code: code.trim().toUpperCase() };
      if (isEdit) await Locations.update(editing.id, payload);
      else await Locations.create(payload);
      toast.success(isEdit ? "Location updated" : "Location added");
      onClose(); onSaved?.();
    } catch (e) { toast.error(apiErrorMessage(e) || "Failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="location-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Edit Location · ${editing.code}` : "Add Location"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs">Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="loc-name-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Code (e.g. JEY)</Label>
            <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} maxLength={5} data-testid="loc-code-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} data-testid="save-location-btn">
            {isEdit ? "Save Changes" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────── Users ───────────

function UsersPanel({ users, locations, refresh, canDelete = true, role = "Admin", embedded = false }) {
  const [editing, setEditing] = useState(null);  // null | "new" | userDoc
  const [tempPwd, setTempPwd] = useState(null);  // { user, temp_password }
  const managers = useMemo(
    () => users.filter((u) => u.role === "SalesManager"),
    [users],
  );

  const remove = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    try {
      await Users.remove(id);
      toast.success("User removed");
      await refresh();
    } catch (e) { toast.error(apiErrorMessage(e) || "Failed"); }
  };

  const toggleActive = async (u) => {
    const willDeactivate = u.is_active !== false;
    const msg = willDeactivate
      ? `Deactivate ${u.name}? They will no longer be able to log in to the portal.`
      : `Re-activate ${u.name}? They will be able to log in again with their existing password.`;
    if (!window.confirm(msg)) return;
    try {
      if (willDeactivate) {
        await Users.deactivate(u.id);
        toast.success(`${u.name} deactivated`);
      } else {
        await Users.activate(u.id);
        toast.success(`${u.name} re-activated`);
      }
      await refresh();
    } catch (e) { toast.error(apiErrorMessage(e) || "Failed"); }
  };

  const resetPwd = async (u) => {
    if (!u.mobile) {
      toast.error("Add a mobile number for this user first");
      return;
    }
    if (!window.confirm(`Reset password for ${u.name}? A new temporary password will be generated and the user will be forced to change it on next login.`)) return;
    try {
      const res = await Users.resetPassword(u.id);
      setTempPwd({ user: u, temp_password: res.temp_password });
      await refresh();
    } catch (e) { toast.error(apiErrorMessage(e) || "Failed"); }
  };

  const locMap = Object.fromEntries(locations.map((l) => [l.id, l]));

  const headerEmbedded = (
    <div className="flex flex-row items-center justify-between gap-2 pb-3">
      <div className="flex items-center gap-2">
        <UsersIcon className="h-4 w-4 text-primary" />
        <div>
          <div className="font-display text-base font-semibold text-slate-900">Users</div>
          <div className="text-[11px] text-slate-500">
            {users.length} total · {users.filter((u) => u.is_active !== false).length} active
          </div>
        </div>
      </div>
      <Button size="sm" className="gap-1" data-testid="add-user-btn"
              onClick={() => setEditing("new")}>
        <Plus className="h-3.5 w-3.5" /> Add User
      </Button>
    </div>
  );

  const listEl = (
    <div className={cn(embedded ? "rounded-md border border-slate-200 bg-white" : "")}>
      {users.length === 0 ? (
        <div className="px-5 py-8 text-center text-sm text-slate-500">No users yet.</div>
      ) : (
        <ul className={cn(embedded ? "divide-y divide-slate-100" : "divide-y divide-border")}>
          {users.map((u) => {
            const isDeactivated = u.is_active === false;
            return (
            <li
              key={u.id}
              className={cn(
                "flex items-center justify-between gap-3",
                embedded ? "px-4 py-2.5" : "px-5 py-3",
                isDeactivated && "bg-slate-50/70",
              )}
              data-testid={`user-row-${u.id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 text-sm font-medium text-slate-900">
                  <span className={isDeactivated ? "text-slate-500 line-through" : ""}>{u.name}</span>
                  {isDeactivated && (
                    <span
                      className="rounded bg-slate-200 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-slate-700"
                      data-testid={`user-deactivated-${u.id}`}
                    >
                      Deactivated
                    </span>
                  )}
                  {u.must_change_password && !isDeactivated && (
                    <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-amber-700">
                      pwd reset
                    </span>
                  )}
                </div>
                <div className="text-xs text-slate-500">
                  {ROLE_LABELS[u.role]}
                  {u.location_ids?.length > 0 && (
                    <> · {u.location_ids.map((id) => locMap[id]?.code).filter(Boolean).join(", ")}</>
                  )}
                  {u.mobile && <> · <span className="font-mono-data">{u.mobile}</span></>}
                  {u.role === "DSE" && u.manager_id && (
                    <> · ↑ {users.find((x) => x.id === u.manager_id)?.name || "Manager"}</>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => toggleActive(u)}
                  className={`h-8 gap-1 px-2 text-[11px] ${isDeactivated ? "text-emerald-700 hover:bg-emerald-50" : "text-slate-500 hover:text-amber-700 hover:bg-amber-50"}`}
                  data-testid={`toggle-active-${u.id}`}
                  title={isDeactivated ? "Re-activate login" : "Deactivate login"}
                >
                  <Power className="h-3.5 w-3.5" />
                  {isDeactivated ? "Activate" : "Deactivate"}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => resetPwd(u)}
                        disabled={isDeactivated}
                        className="h-8 px-2 text-[11px] text-slate-500 hover:text-primary disabled:opacity-40"
                        data-testid={`reset-pwd-${u.id}`}>
                  Reset Pwd
                </Button>
                <Button size="icon" variant="ghost" onClick={() => setEditing(u)}
                        className="h-8 w-8 text-slate-400 hover:text-primary"
                        data-testid={`edit-user-${u.id}`}>
                  <Pencil className="h-4 w-4" />
                </Button>
                {canDelete && (
                  <Button size="icon" variant="ghost" onClick={() => remove(u.id)}
                          className="h-8 w-8 text-slate-400 hover:text-red-600"
                          data-testid={`delete-user-${u.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </li>
            );
          })}
        </ul>
      )}
    </div>
  );

  const dialogs = (
    <>
      <UserDialog
        open={editing !== null}
        editing={editing === "new" ? null : editing}
        locations={locations}
        managers={managers}
        onClose={() => setEditing(null)}
        onSaved={refresh}
        onTempPassword={(payload) => setTempPwd(payload)}
        actorRole={role}
      />
      <TempPasswordDialog
        payload={tempPwd}
        onClose={() => setTempPwd(null)}
      />
    </>
  );

  if (embedded) {
    return (
      <div className="space-y-3" data-testid="users-panel-embedded">
        {headerEmbedded}
        {listEl}
        {dialogs}
      </div>
    );
  }

  return (
    <Card className="border-slate-200">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <div className="flex items-center gap-2">
          <UsersIcon className="h-4 w-4 text-primary" />
          <CardTitle className="font-display text-lg">Users</CardTitle>
        </div>
        <Button size="sm" className="gap-1" data-testid="add-user-btn-alt"
                onClick={() => setEditing("new")}>
          <Plus className="h-3.5 w-3.5" /> Add
        </Button>
      </CardHeader>
      <CardContent className="p-0">{listEl}</CardContent>
      {dialogs}
    </Card>
  );
}

function TempPasswordDialog({ payload, onClose }) {
  const copy = () => {
    if (!payload?.temp_password) return;
    navigator.clipboard?.writeText(payload.temp_password).then(
      () => toast.success("Password copied"),
      () => toast.error("Copy failed — please copy manually"),
    );
  };
  return (
    <Dialog open={!!payload} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="temp-password-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Password Generated</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-slate-600">
            Share this one-time password with <strong>{payload?.user?.name}</strong>. They will be
            asked to set a new password on their next login. This password will not be shown again.
          </p>
          <div className="rounded-md border border-slate-300 bg-slate-50 p-3 text-center font-mono-data text-lg font-semibold tracking-widest text-slate-900"
               data-testid="temp-password-value">
            {payload?.temp_password || ""}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={copy} data-testid="temp-password-copy-btn">
            Copy
          </Button>
          <Button onClick={onClose} data-testid="temp-password-done-btn">Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UserDialog({ open, editing, locations, managers = [], onClose, onSaved, onTempPassword, actorRole = "Admin" }) {
  const isEdit = !!editing;
  const [form, setForm] = useState({
    name: "", role: "DSE", phone: "", mobile: "", password: "", location_ids: [],
  });

  useEffect(() => {
    if (open) {
      setForm({
        name: editing?.name || "",
        role: editing?.role || "DSE",
        phone: editing?.phone || "",
        mobile: editing?.mobile || "",
        password: "",
        location_ids: editing?.location_ids || [],
        manager_id: editing?.manager_id || "",
      });
    }
  }, [open, editing]);

  const toggleLoc = (id) => {
    setForm((f) => ({
      ...f,
      location_ids: f.location_ids.includes(id)
        ? f.location_ids.filter((x) => x !== id)
        : [...f.location_ids, id],
    }));
  };

  const genPwd = () => {
    // 8-char letters+digits, ambiguous chars removed.
    const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
    let pwd = "";
    for (let i = 0; i < 8; i++) {
      pwd += alphabet[Math.floor(Math.random() * alphabet.length)];
    }
    setForm((f) => ({ ...f, password: pwd }));
  };

  const save = async () => {
    if (!form.name.trim() || !form.role) return toast.error("Name and role required");
    const mobile = (form.mobile || "").replace(/\D/g, "").slice(-10);
    if (mobile && mobile.length !== 10) return toast.error("Mobile must be exactly 10 digits");

    const payload = {
      name: form.name.trim(),
      role: form.role,
      phone: form.phone?.trim() || null,
      mobile: mobile || null,
      location_ids: form.location_ids,
      manager_id: form.role === "DSE" ? (form.manager_id || null) : null,
    };

    try {
      if (isEdit) {
        await Users.update(editing.id, payload);
        toast.success("User updated");
        onClose();
        onSaved?.();
      } else {
        // Create flow — require password if mobile is set so user can log in.
        if (mobile && !form.password) return toast.error("Password is required when setting a mobile");
        if (form.password && form.password.length < 8) return toast.error("Password must be ≥ 8 characters");
        const createPayload = { ...payload, password: form.password || null };
        const created = await Users.create(createPayload);
        toast.success("User added");
        onClose();
        onSaved?.();
        if (mobile && form.password) {
          // Show the password once for the Admin to share.
          onTempPassword?.({ user: created, temp_password: form.password });
        }
      }
    } catch (e) { toast.error(apiErrorMessage(e) || "Failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="user-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? `Edit User · ${editing.name}` : "Add User"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs">Name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                   data-testid="user-name-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Role</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ROLES
                  // Sales Managers cannot create Admin or another Sales
                  // Manager — mirrors the backend guard.
                  .filter((r) => actorRole === "Admin" || (r !== "Admin" && r !== "SalesManager"))
                  .map((r) => (
                    <SelectItem key={r} value={r} data-testid={`user-role-option-${r}`}>
                      {ROLE_LABELS[r]}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          {form.role === "DSE" && (
            <div className="space-y-1.5">
              <Label className="text-xs">Reports to (Sales Manager)</Label>
              <Select
                value={form.manager_id || "__none__"}
                onValueChange={(v) => setForm({ ...form, manager_id: v === "__none__" ? "" : v })}
              >
                <SelectTrigger data-testid="user-manager-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__" data-testid="user-manager-option-none">— None —</SelectItem>
                  {managers.map((mgr) => (
                    <SelectItem key={mgr.id} value={mgr.id}
                                data-testid={`user-manager-option-${mgr.id}`}>
                      {mgr.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-slate-500">
                The Sales Manager will see this DSE's sales, tasks and team performance.
                Leave as "None" if this DSE doesn't report to anyone yet.
              </p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Login Mobile <span className="text-rose-500">*</span></Label>
              <Input
                value={form.mobile}
                onChange={(e) => setForm({ ...form, mobile: e.target.value.replace(/\D/g, "").slice(0, 10) })}
                placeholder="10 digits"
                inputMode="numeric"
                maxLength={10}
                data-testid="user-mobile-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Alt Phone</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                     data-testid="user-phone-input" />
            </div>
          </div>

          {!isEdit && (
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label className="text-xs">Initial Password</Label>
                <Button type="button" size="sm" variant="ghost" onClick={genPwd}
                        className="h-7 px-2 text-[11px] text-primary"
                        data-testid="user-generate-pwd-btn">
                  Generate
                </Button>
              </div>
              <Input
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="≥ 8 characters"
                data-testid="user-password-input"
              />
              <p className="text-[10px] text-slate-500">
                The password will be shown once after creating the user — share it with them securely.
                They can change it from the user menu after logging in.
              </p>
            </div>
          )}

          {isEdit && (
            <p className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] text-slate-600">
              To change the password, use <strong>Reset Pwd</strong> on the user row — a new
              one-time password is generated and the user is forced to set their own on next login.
            </p>
          )}

          <div className="space-y-1.5">
            <Label className="text-xs">Assigned Locations</Label>
            <div className="grid grid-cols-2 gap-2 rounded-md border border-border p-3"
                 data-testid="user-locations-grid">
              {locations.map((l) => (
                <label key={l.id} className="flex cursor-pointer items-center gap-2 text-sm">
                  <Checkbox
                    checked={form.location_ids.includes(l.id)}
                    onCheckedChange={() => toggleLoc(l.id)}
                    data-testid={`user-loc-${l.code}`}
                  />
                  <span>{l.name} <span className="font-mono-data text-[10px] text-slate-400">({l.code})</span></span>
                </label>
              ))}
            </div>
            <p className="text-[10px] text-slate-500">
              Tick every showroom this user should appear in. Manpower can be reassigned anytime.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} data-testid="save-user-btn">
            {isEdit ? "Save Changes" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
